/**
 * Following: fans you follow, then the requests you sent that are still
 * waiting, under their own heading (ISSUE-1372, v1's following tab). The
 * server sorts the two groups; this only splits them where the state
 * changes. Unfollow and Withdraw both ask first, through the same
 * `useFollowToggle` the profile's button uses.
 */
import { useFanFollowFollowingList } from "@sportsmet/profile-sdk/private";
import { Button } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import {
  FOLLOWING_EMPTY_BODY,
  FOLLOWING_EMPTY_HEADLINE,
  MESSAGE_LABEL,
  messageLabelFor,
  UNFOLLOW_LABEL,
  WAITING_SECTION_TITLE,
  WITHDRAW_LABEL,
} from "./followCopy";
import { followingSinceLine, requestSentLine } from "./followDates";
import { FOLLOW_LIST_CLASS, FollowListFrame } from "./FollowListFrame";
import {
  canShowMore,
  FOLLOW_LIST_PAGE_SIZE,
  nextFollowListLimit,
} from "./followListPaging";
import { FollowPersonRow } from "./FollowPersonRow";
import { useFollowToggle } from "./useFollowToggle";
import { useShowListError } from "./useFollowListQueryErrors";
import { useMessageShortcut } from "./useMessageShortcut";

type typeFollowingItem = {
  accountId: string;
  username: string;
  displayName: string;
  photoUrl?: string | null;
  followState: "requested" | "following";
  since: string;
};

const UndoFollowButton = ({ item }: { item: typeFollowingItem }): ReactNode => {
  const follow = useFollowToggle({
    accountId: item.accountId,
    displayName: item.displayName,
    state: item.followState,
  });
  return (
    <Button
      size="sm"
      variant="ghost"
      loading={follow.isPending}
      loadingLabel={
        item.followState === "following" ? "Unfollowing" : "Withdrawing"
      }
      onClick={() => {
        void follow.toggle();
      }}
      data-testid={`follow-following-undo-${item.accountId}`}
    >
      {item.followState === "following" ? UNFOLLOW_LABEL : WITHDRAW_LABEL}
    </Button>
  );
};

export const FollowingTab = (): ReactNode => {
  const [limit, setLimit] = useState(FOLLOW_LIST_PAGE_SIZE);
  const list = useFanFollowFollowingList({ limit });
  const shortcut = useMessageShortcut();
  useShowListError(list);

  const items: typeFollowingItem[] = list.data?.data?.items ?? [];
  const following = items.filter((item) => item.followState === "following");
  const waiting = items.filter((item) => item.followState === "requested");

  const renderRow = (item: typeFollowingItem): ReactNode => (
    <FollowPersonRow
      key={item.accountId}
      person={item}
      caption={
        item.followState === "following"
          ? followingSinceLine(item.since)
          : requestSentLine(item.since)
      }
      testId={`follow-following-${item.accountId}`}
      actions={
        <>
          {item.followState === "following" ? (
            <Button
              size="sm"
              variant="secondary"
              aria-label={messageLabelFor(item.displayName)}
              loading={shortcut.openingAccountId === item.accountId}
              loadingLabel="Opening chat"
              onClick={() => {
                shortcut.message(item.accountId);
              }}
              data-testid={`follow-following-message-${item.accountId}`}
            >
              {MESSAGE_LABEL}
            </Button>
          ) : null}
          <UndoFollowButton item={item} />
        </>
      }
    />
  );

  return (
    <FollowListFrame
      isLoading={list.isPending}
      isEmpty={items.length === 0}
      emptyHeadline={FOLLOWING_EMPTY_HEADLINE}
      emptyBody={FOLLOWING_EMPTY_BODY}
      canShowMore={canShowMore({
        limit,
        nextCursor: list.data?.data?.nextCursor,
      })}
      onShowMore={() => {
        setLimit(nextFollowListLimit);
      }}
      isShowingMore={list.isFetching}
      testId="follow-following"
    >
      {following.length > 0 ? (
        <ul className={FOLLOW_LIST_CLASS}>{following.map(renderRow)}</ul>
      ) : null}
      {waiting.length > 0 ? (
        <section className="flex flex-col gap-2" data-testid="follow-waiting">
          <h2 className="text-body-strong text-ink">{WAITING_SECTION_TITLE}</h2>
          <ul className={FOLLOW_LIST_CLASS}>{waiting.map(renderRow)}</ul>
        </section>
      ) : null}
    </FollowListFrame>
  );
};
